import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FolderPlus,
  Upload,
  ChevronRight,
  Folder,
  ArrowUpDown,
  LayoutGrid,
  List as ListIcon,
  Check,
} from 'lucide-react'
import AppLayout from '../../components/layout/AppLayout'
import FilesTree from '../../components/files/FilesTree'
import FolderView, { type FolderViewHandle } from '../../components/files/FolderView'
import {
  type FilesViewMode,
  type FilesSortKey,
  SORT_OPTIONS,
  loadViewMode,
  saveViewMode,
  loadSortKey,
  saveSortKey,
} from '../../components/files/filesView'
import { useActiveServer } from '../../hooks/useActiveServer'
import type { FilesFolder, FilesPathItem } from '../../services/api'

export default function FilesWorkspacePage() {
  const server = useActiveServer()
  const serverId = server?.id ?? ''

  const [searchParams, setSearchParams] = useSearchParams()
  const folderId = searchParams.get('folder') ?? ''

  const folderViewRef = useRef<FolderViewHandle>(null)
  const [loadedFolder, setLoadedFolder] = useState<FilesFolder | null>(null)
  const [path, setPath] = useState<FilesPathItem[]>([])

  const [viewMode, setViewMode] = useState<FilesViewMode>(loadViewMode)
  const [sortKey, setSortKey] = useState<FilesSortKey>(loadSortKey)
  const [sortOpen, setSortOpen] = useState(false)

  const selectFolder = (id: string) => {
    setLoadedFolder(null)
    setPath([])
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('folder', id)
      return next
    })
  }

  const clearFolder = () => {
    setLoadedFolder(null)
    setPath([])
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('folder')
      return next
    })
  }

  const changeViewMode = (mode: FilesViewMode) => {
    setViewMode(mode)
    saveViewMode(mode)
  }

  const changeSort = (key: FilesSortKey) => {
    setSortKey(key)
    saveSortKey(key)
    setSortOpen(false)
  }

  const activeSort = SORT_OPTIONS.find((o) => o.key === sortKey) ?? SORT_OPTIONS[0]

  return (
    <AppLayout secondarySidebar={<FilesTree serverId={serverId} />}>
      {/* Brow — contract: h-14 shrink-0 px-6 flex items-center gap-3 border-b border-border bg-card */}
      <div className="h-14 shrink-0 px-6 flex items-center gap-3 border-b border-border bg-card">
        {/* Breadcrumb — full path, each ancestor clickable, current not clickable */}
        <nav
          aria-label="Folder path"
          className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto"
        >
          <button
            onClick={clearFolder}
            className="flex items-center gap-1.5 shrink-0 text-sm font-medium text-foreground hover:text-primary transition"
          >
            <Folder size={16} className="text-primary shrink-0" />
            <span>Files</span>
          </button>
          {path.map((item, i) => {
            const isCurrent = i === path.length - 1
            return (
              <span key={item.id} className="flex items-center gap-1 shrink-0">
                <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                {isCurrent ? (
                  <span
                    aria-current="page"
                    className="text-sm text-foreground font-medium truncate max-w-[220px]"
                    title={item.name}
                  >
                    {item.name}
                  </span>
                ) : (
                  <button
                    onClick={() => selectFolder(item.id)}
                    className="text-sm text-muted-foreground hover:text-foreground transition truncate max-w-[180px]"
                    title={item.name}
                  >
                    {item.name}
                  </button>
                )}
              </span>
            )
          })}
        </nav>

        {/* Right — view controls + actions (only when inside a folder) */}
        {folderId && (
          <div className="flex items-center gap-2 shrink-0">
            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setSortOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                title="Sort"
                className="flex items-center gap-1.5 h-8 px-3 rounded-md text-sm bg-muted border border-border text-foreground hover:bg-card transition"
              >
                <ArrowUpDown size={14} />
                <span className="hidden md:inline">{activeSort.label}</span>
              </button>
              {sortOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setSortOpen(false)} />
                  <ul
                    role="listbox"
                    className="absolute right-0 mt-1 z-30 w-48 py-1 rounded-md border border-border bg-card shadow-lg"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <li key={opt.key}>
                        <button
                          role="option"
                          aria-selected={opt.key === sortKey}
                          onClick={() => changeSort(opt.key)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-muted transition text-left"
                        >
                          <span>{opt.label}</span>
                          {opt.key === sortKey && <Check size={14} className="text-primary shrink-0" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* View mode toggle */}
            <div className="flex items-center h-8 rounded-md bg-muted border border-border overflow-hidden">
              <button
                onClick={() => changeViewMode('list')}
                aria-pressed={viewMode === 'list'}
                title="List view"
                className={`flex items-center justify-center h-full w-8 transition ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <ListIcon size={15} />
              </button>
              <button
                onClick={() => changeViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                title="Grid view"
                className={`flex items-center justify-center h-full w-8 transition ${
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid size={15} />
              </button>
            </div>

            {/* Actions — only with write access */}
            {loadedFolder?.write && (
              <>
                <button
                  onClick={() => folderViewRef.current?.triggerNewFolder()}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-md text-sm bg-muted border border-border text-foreground hover:bg-card transition"
                >
                  <FolderPlus size={14} />
                  <span className="hidden sm:inline">New folder</span>
                </button>
                <button
                  onClick={() => folderViewRef.current?.triggerUpload()}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 transition"
                >
                  <Upload size={14} />
                  <span className="hidden sm:inline">Upload</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Content — flex-1 min-h-0 overflow-y-auto */}
      <div className="flex-1 min-h-0 flex flex-col">
        {folderId ? (
          <FolderView
            ref={folderViewRef}
            folderId={folderId}
            viewMode={viewMode}
            sortKey={sortKey}
            onFolderSelect={selectFolder}
            onFolderLoaded={setLoadedFolder}
            onPathLoaded={setPath}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
            <Folder size={48} className="text-muted-foreground" />
            <p className="text-base font-medium text-foreground">Select a folder</p>
            <p className="text-sm text-muted-foreground max-w-xs">
              Choose a folder from the sidebar to browse its files, or use company / department / personal folders to get started.
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
